import { Fragment, useEffect, useState } from "react"
import type { HeatmapCell } from "../types/analytics.types"
import { HEATMAP_COLORS, TIME_SLOTS, DAYS } from "../constants/analyticsConstants"
import { ChartEmptyOverlay } from "../../../components/ChartEmptyOverlay"

interface HeatmapGridProps {
  data?: HeatmapCell[]
  emptyTitle?: string
  emptyMessage?: string
}

export function HeatmapGrid({ data, emptyTitle, emptyMessage }: HeatmapGridProps): JSX.Element {
  const [visible, setVisible] = useState(false)
  const isEmpty = !data || data.length === 0

  useEffect(() => {
    setVisible(true)
  }, [])

  const getCell = (timeSlot: string, day: string): HeatmapCell | undefined =>
    data?.find((cell) => cell.timeSlot === timeSlot && cell.day === day)

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
      <h3 className="mb-4 text-base font-semibold text-gray-900">Incident by Time and Day</h3>
      {isEmpty ? (
        <ChartEmptyOverlay title={emptyTitle} message={emptyMessage} heightClassName="h-64" />
      ) : (
        <div className="overflow-x-auto">
        <div className="grid min-w-[560px] grid-cols-8 gap-1.5">
          <div className="text-xs font-medium text-gray-500">Time of Day</div>
          {DAYS.map((day) => (
            <div key={day} className="text-center text-xs font-medium text-gray-500">
              {day}
            </div>
          ))}

          {TIME_SLOTS.map((timeSlot, rowIndex) => (
            <Fragment key={timeSlot}>
              <div className="flex items-center text-xs text-gray-600 pr-2">{timeSlot}</div>
              {DAYS.map((day, colIndex) => {
                const cell = getCell(timeSlot, day)
                const count = cell?.count ?? 0
                const intensity = cell?.intensity ?? 0
                return (
                  <div
                    key={`${timeSlot}-${day}`}
                    data-testid="heatmap-cell"
                    className="flex h-10 items-center justify-center rounded text-xs font-medium"
                    style={{
                      backgroundColor: HEATMAP_COLORS[intensity] ?? HEATMAP_COLORS[0],
                      color: intensity >= 4 ? "#FFFFFF" : "#374151",
                      opacity: visible ? 1 : 0,
                      transition: `opacity 0.5s ease ${(rowIndex * 7 + colIndex) * 30}ms`,
                    }}
                    title={`${timeSlot} ${day}: ${count} incidents`}
                  >
                    {count}
                  </div>
                )
              })}
            </Fragment>
          ))}
        </div>
        </div>
      )}
    </div>
  )
}
